/**
 * Phase 94 — borderless-until-hover inline table cells, spreadsheet style.
 * `EditableText` covers text/number/date inputs; `EditableSelect` covers
 * dropdowns (with an optional "+ Add new" trailing item for dynamic taxonomy).
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { formatDate } from "@/lib/dateFormat";

const cellBase = "h-7 text-xs border border-transparent bg-transparent hover:border-slate-300 focus:border-[#F54800] focus:bg-white rounded-sm px-1.5 transition-colors";
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Oct 2026 — ledger-speed cells: text inputs keep the in-progress string in
// local state and commit on blur / Enter (`commitOnBlur`), so the parent
// ledger isn't re-rendered on every keystroke. Every cell is React.memo'd;
// the ledgers pass stable per-row callbacks so unchanged rows skip renders.
function useDraft(value, onChange, commitOnBlur) {
  const [draft, setDraft] = React.useState(value ?? "");
  const focused = React.useRef(false);
  React.useEffect(() => { if (!focused.current) setDraft(value ?? ""); }, [value]);
  const handleChange = (v) => { setDraft(v); if (!commitOnBlur) onChange(v); };
  const commit = () => { if (commitOnBlur && (draft ?? "") !== (value ?? "")) onChange(draft); };
  return {
    draft,
    onFocus: () => { focused.current = true; },
    onBlur: () => { focused.current = false; commit(); },
    handleChange,
    commit,
  };
}

export const EditableText = React.memo(function EditableText({ value, onChange, className = "", testId, placeholder, type = "text", align = "left", commitOnBlur = false }) {
  const d = useDraft(value, onChange, commitOnBlur);
  return (
    <Input
      type={type}
      value={d.draft}
      onChange={(e) => d.handleChange(e.target.value)}
      onFocus={d.onFocus}
      onBlur={d.onBlur}
      onKeyDown={(e) => { if (e.key === "Enter") { d.commit(); e.currentTarget.blur(); } }}
      className={`${cellBase} ${align === "right" ? "text-right font-mono" : ""} ${className}`}
      placeholder={placeholder}
      data-testid={testId}
    />
  );
});

// Phase 97 Batch 2 — "No data cutoff": an auto-growing textarea for long
// descriptions/narrations that need real multi-line wrapping (a plain
// <input> can never wrap, no matter how wide the column is).
export const EditableTextArea = React.memo(function EditableTextArea({ value, onChange, className = "", testId, placeholder, commitOnBlur = true }) {
  const ref = React.useRef(null);
  const d = useDraft(value, onChange, commitOnBlur);
  const grow = (el) => { if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } };
  React.useEffect(() => { grow(ref.current); }, [d.draft]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={d.draft}
      onChange={(e) => { d.handleChange(e.target.value); grow(e.target); }}
      onFocus={d.onFocus}
      onBlur={d.onBlur}
      placeholder={placeholder}
      className={`${cellBase} resize-none overflow-hidden leading-4 py-1.5 block w-full whitespace-pre-wrap ${className}`}
      data-testid={testId}
    />
  );
});

export const EditableSelect = React.memo(function EditableSelect({ value, onChange, options, testId, placeholder = "—", onAddNew, addNewLabel = "+ Add new…" }) {
  // Accept either a flat string[] or a {value,label}[] — normalize once here
  // so every caller can pass whichever is convenient.
  const normalized = [];
  const seen = new Set();
  (options || []).forEach((o) => {
    const opt = typeof o === "string" ? { value: o, label: o } : o;
    if (opt && !seen.has(opt.value)) { seen.add(opt.value); normalized.push(opt); }
  });
  // If the row's current value isn't in the option list (e.g. a legacy
  // status like "issued"/"pending" that predates this taxonomy), inject it
  // so the cell still shows something instead of rendering blank.
  const hasCurrent = value && normalized.some((o) => o.value === value);
  const withCurrent = hasCurrent || !value ? normalized : [{ value, label: value }, ...normalized];
  // Phase 98 — "no data cutoff": long labels (client/company names, service
  // types) truncate with an ellipsis instead of overflowing the cell, and a
  // native title tooltip reveals the full value on hover.
  const currentLabel = withCurrent.find((o) => o.value === value)?.label;
  return (
    <Select
      value={value || ""}
      onValueChange={(v) => { if (v === "__add_new__") { onAddNew?.(); return; } onChange(v); }}
    >
      <SelectTrigger className={`${cellBase} w-full`} data-testid={testId} title={currentLabel || undefined}>
        <SelectValue placeholder={placeholder} className="truncate block" />
      </SelectTrigger>
      <SelectContent>
        {withCurrent.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        {onAddNew && (
          <div className="border-t border-slate-100 mt-1 pt-1">
            <SelectItem value="__add_new__" className="text-[#F54800] font-medium">{addNewLabel}</SelectItem>
          </div>
        )}
      </SelectContent>
    </Select>
  );
});

// Phase 95 — native <input type="date"> replaced with the app's own
// Popover + Calendar picker, matching the rest of the portal's date UX
// (and the "2nd Mar" / "2nd Mar 2025" display convention via `formatDate`).
// Value/onChange stay plain ISO "YYYY-MM-DD" strings, so callers don't change.
export const EditableDate = React.memo(function EditableDate({ value, onChange, testId, placeholder = "Undated", className = "" }) {
  const [open, setOpen] = React.useState(false);
  const selected = value && isValid(parseISO(value)) ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`${cellBase} w-full inline-flex items-center gap-1 truncate ${!selected ? "text-slate-400 italic" : "text-slate-700"} ${className}`}
          data-testid={testId}
          title={selected ? formatDate(selected) : placeholder}
        >
          <CalendarIcon size={11} className="shrink-0 text-slate-400" />
          {selected ? formatDate(selected) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }}
          initialFocus
        />
        {selected && (
          <div className="border-t border-slate-100 p-1.5">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="w-full text-center text-xs text-slate-500 hover:text-red-600 py-1 rounded-sm hover:bg-slate-50"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              Clear → Undated
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});

// Phase 95 — native <input type="month"> replaced with a compact
// Month + Year dropdown pair inside a popover. Value/onChange stay plain
// "YYYY-MM" strings.
export const EditableMonth = React.memo(function EditableMonth({ value, onChange, testId, placeholder = "—" }) {
  const [open, setOpen] = React.useState(false);
  const [y, m] = (value || "").split("-");
  const year = y ? Number(y) : new Date().getFullYear();
  const monthIdx = m ? Number(m) - 1 : new Date().getMonth();
  const baseYears = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 4 + i);
  // A stored value from before this 6-year window (e.g. an old backfilled
  // row) must still show correctly instead of rendering a blank trigger.
  const years = baseYears.includes(year) ? baseYears : [year, ...baseYears].sort();
  const label = value ? `${MONTH_NAMES[monthIdx].slice(0, 3)} ${year}` : placeholder;
  const apply = (nextYear, nextMonthIdx) => onChange(`${nextYear}-${String(nextMonthIdx + 1).padStart(2, "0")}`);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${cellBase} w-full inline-flex items-center gap-1 truncate ${!value ? "text-slate-400 italic" : "text-slate-700"}`} data-testid={testId} title={value ? label : placeholder}>
          <CalendarIcon size={11} className="shrink-0 text-slate-400" /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex gap-1.5">
          <Select value={String(monthIdx)} onValueChange={(v) => apply(year, Number(v))}>
            <SelectTrigger className="h-8 text-xs rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((mn, i) => <SelectItem key={mn} value={String(i)}>{mn.slice(0, 3)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => apply(Number(v), monthIdx)}>
            <SelectTrigger className="h-8 text-xs rounded-sm w-20"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((yr) => <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {value && (
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-center text-xs text-slate-500 hover:text-red-600 py-1 mt-1.5 rounded-sm hover:bg-slate-50 border-t border-slate-100 pt-1.5"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
});
